const DAY_MS = 24 * 60 * 60 * 1000;

export function parseISODate(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`);
}

export function daysBetweenInclusive(startDate, endDate) {
  const start = parseISODate(startDate).getTime();
  const end = parseISODate(endDate).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 1;
  return Math.max(1, Math.round((end - start) / DAY_MS) + 1);
}

/** Pick bucket size from visible span — semantic zoom core. */
export function pickGranularity(spanDays, pointCount = spanDays) {
  if (spanDays <= 31 || pointCount <= 31) return 'day';
  if (spanDays <= 120) return 'week';
  return 'month';
}

function startOfWeek(dateStr) {
  const date = parseISODate(dateStr);
  const weekday = date.getUTCDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  date.setUTCDate(date.getUTCDate() + mondayOffset);
  return date.toISOString().slice(0, 10);
}

function startOfMonth(dateStr) {
  return `${dateStr.slice(0, 7)}-01`;
}

function formatBucketLabel(dateStr, granularity) {
  if (granularity === 'month') return dateStr.slice(0, 7);
  if (granularity === 'week') return dateStr.slice(5);
  return dateStr.slice(5);
}

function normalizePoint(point, valueKey) {
  return {
    date: point.date,
    value: Number(point[valueKey] ?? point.value ?? 0),
    invoiceCount: Number(point.invoiceCount || 0),
    averageOrderValue: point.averageOrderValue != null ? Number(point.averageOrderValue) : null,
  };
}

/**
 * Aggregate daily points into week/month buckets (sum revenue, sum invoices, weighted AOV).
 */
export function aggregateTimeSeries(points, granularity, valueKey = 'value') {
  if (!points?.length) return [];
  if (granularity === 'day') {
    return points.map((point) => {
      const normalized = normalizePoint(point, valueKey);
      return {
        ...normalized,
        label: formatBucketLabel(normalized.date, 'day'),
        granularity: 'day',
      };
    });
  }

  const buckets = new Map();

  for (const raw of points) {
    const point = normalizePoint(raw, valueKey);
    const bucketDate = granularity === 'week' ? startOfWeek(point.date) : startOfMonth(point.date);
    const bucket = buckets.get(bucketDate) || {
      date: bucketDate,
      value: 0,
      invoiceCount: 0,
      _aovRevenue: 0,
    };

    bucket.value += point.value;
    bucket.invoiceCount += point.invoiceCount;
    if (point.averageOrderValue != null && point.invoiceCount > 0) {
      bucket._aovRevenue += point.averageOrderValue * point.invoiceCount;
    }
    buckets.set(bucketDate, bucket);
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((bucket) => ({
      date: bucket.date,
      value: bucket.value,
      invoiceCount: bucket.invoiceCount,
      averageOrderValue:
        bucket.invoiceCount > 0 ? bucket._aovRevenue / bucket.invoiceCount : null,
      label: formatBucketLabel(bucket.date, granularity),
      granularity,
    }));
}

export function sliceByPercent(points, startPercent, endPercent) {
  if (!points.length) return [];
  const startIdx = Math.floor((startPercent / 100) * points.length);
  const endIdx = Math.max(startIdx + 1, Math.ceil((endPercent / 100) * points.length));
  return points.slice(startIdx, endIdx);
}

export function buildSemanticSeries(rawPoints, startPercent = 0, endPercent = 100, valueKey = 'value') {
  const window = sliceByPercent(rawPoints, startPercent, endPercent);
  if (!window.length) {
    return { points: [], granularity: 'day', spanDays: 0 };
  }

  const spanDays = daysBetweenInclusive(window[0].date, window[window.length - 1].date);
  const granularity = pickGranularity(spanDays, window.length);
  const points = aggregateTimeSeries(window, granularity, valueKey);

  return { points, granularity, spanDays };
}

/**
 * Aggregate multi-metric daily rows (e.g. cmp/pos/app) into week/month buckets.
 * Sums each value key independently within the bucket.
 */
export function aggregateMultiSeries(points, granularity, valueKeys = ['cmp', 'pos', 'app']) {
  if (!points?.length) return [];
  if (granularity === 'day') {
    return points.map((point) => {
      const row = { date: point.date, label: formatBucketLabel(point.date, 'day'), granularity: 'day' };
      for (const key of valueKeys) {
        row[key] = Number(point[key] || 0);
      }
      return row;
    });
  }

  const buckets = new Map();

  for (const point of points) {
    const bucketDate = granularity === 'week' ? startOfWeek(point.date) : startOfMonth(point.date);
    const bucket = buckets.get(bucketDate) || {
      date: bucketDate,
      ...Object.fromEntries(valueKeys.map((key) => [key, 0])),
    };
    for (const key of valueKeys) {
      bucket[key] += Number(point[key] || 0);
    }
    buckets.set(bucketDate, bucket);
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((bucket) => ({
      ...bucket,
      label: formatBucketLabel(bucket.date, granularity),
      granularity,
    }));
}

export function buildSemanticMultiSeries(
  rawPoints,
  startPercent = 0,
  endPercent = 100,
  valueKeys = ['cmp', 'pos', 'app']
) {
  const sorted = [...(rawPoints || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const window = sliceByPercent(sorted, startPercent, endPercent);
  if (!window.length) {
    return { points: [], granularity: 'day', spanDays: 0 };
  }

  const spanDays = daysBetweenInclusive(window[0].date, window[window.length - 1].date);
  const granularity = pickGranularity(spanDays, window.length);
  const points = aggregateMultiSeries(window, granularity, valueKeys);

  return { points, granularity, spanDays };
}

/** Prefer a recent window when the series is very long so first paint stays readable. */
export function defaultZoomRangeForLength(pointCount) {
  if (pointCount > 365) return { start: 70, end: 100 };
  if (pointCount > 120) return { start: 50, end: 100 };
  return { start: 0, end: 100 };
}

export function granularityLabel(granularity) {
  if (granularity === 'week') return 'Weekly';
  if (granularity === 'month') return 'Monthly';
  return 'Daily';
}
